import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import { useStyles } from '../styles';
import { useAgentProfileCreateControllerAgentProfileCreateForUsersMutation } from '@shared/services/apiService';
import { AddAPTab, AddAgentPromptFormValue } from '../types';
import { AddAgentProfileForm } from './AgentProfileForm';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import { IconChevronDown } from '@tabler/icons-react';
import { useAgentProfiles } from '../hooks/useAgentProfiles';
import { mapAPResponseDtoToFormValue } from '../utils';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';

interface AddAgentProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddAgentProfileModal: React.FC<AddAgentProfileModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError } = useErrorHandler();
  const { customerTemplateAgentProfiles, data: AllAgentProfiles } = useAgentProfiles();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<AddAPTab>(AddAPTab.SELECT_AP);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');

  const selectedTemplate = AllAgentProfiles?.find((t) => t.name === selectedTemplateName);

  const [createAgentProfile] = useAgentProfileCreateControllerAgentProfileCreateForUsersMutation();

  const formMethods = useForm<AddAgentPromptFormValue>({
    defaultValues: {
      name: '',
      prompt: '',
      firstMessage: '',
      firstMessageInterruptionsEnabled: false,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset } = formMethods;

  const resetTemplate = () => {
    reset({
      name: '',
      prompt: '',
      firstMessage: '',
      firstMessageInterruptionsEnabled: false,
    });
    setSelectedTemplateName('');
  };

  useEffect(() => {
    if (open) {
      resetTemplate();
      setStep(AddAPTab.SELECT_AP);
    }
  }, [open, reset]);

  const handleResetAndSetStep = (step: AddAPTab) => {
    resetTemplate();
    setStep(step);
  };

  const handleClose = () => {
    handleResetAndSetStep(AddAPTab.SELECT_AP);
    onClose();
  };

  const onSubmit = async (data: AddAgentPromptFormValue) => {
    try {
      setIsSubmitting(true);
      const normalizedFirstMessage = data.firstMessage.trim();

      await createAgentProfile({
        agentProfileCreateRequestDto: {
          ...data,
          firstMessage: normalizedFirstMessage === '' ? null : normalizedFirstMessage,
          firstMessageInterruptionsEnabled: data.firstMessageInterruptionsEnabled,
        },
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('assistantsPage.success.title'),
          description:
            t('assistantsPage.success.created', { name: data.name }) ||
            `Agent profile "${data.name}" created successfully`,
        },
      });

      handleClose();
      onSuccess();
    } catch (err) {
      showError({
        title: t('assistantsPage.errors.createTitle'),
        error: err,
        suggestion: t('assistantsPage.errors.createSuggestion'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.modalPaper }}>
      <DialogTitle sx={{ flexShrink: 0 }}>{t('assistantsPage.modals.add.title')}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} className={classes.modalForm}>
        <FormProvider {...formMethods}>
          <DialogContent
            dividers
            className={classes.modalContent}
            sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {step === AddAPTab.SELECT_AP && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 3 }}>
                  {t('assistantsPage.modals.add.description')}
                </Typography>
                <CustomSelect
                  value={selectedTemplateName}
                  onChange={(e: SelectChangeEvent) => setSelectedTemplateName(e.target.value)}
                  displayEmpty
                  className={classes.agent_profile_template_select}
                  IconComponent={IconChevronDown}
                  renderValue={(value: string) =>
                    value ? (
                      value
                    ) : (
                      <Typography color="text.secondary">
                        {t('assistantsPage.modals.add.selectPlaceholder')}
                      </Typography>
                    )
                  }>
                  {customerTemplateAgentProfiles?.map((template) => (
                    <MenuItem key={template.name} value={template.name}>
                      {template.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </>
            )}
            {step === AddAPTab.AP_FORM && (
              <AddAgentProfileForm template={mapAPResponseDtoToFormValue(selectedTemplate)} />
            )}
          </DialogContent>

          <DialogActions className={classes.modalActions} sx={{ flexShrink: 0 }}>
            {step === AddAPTab.SELECT_AP ? (
              <>
                <Button onClick={handleClose} disabled={isSubmitting}>
                  {t('assistantsPage.modals.cancel')}
                </Button>
                <Button
                  onClick={() => setStep(AddAPTab.AP_FORM)}
                  variant="contained"
                  disabled={isSubmitting || !selectedTemplateName}>
                  {t('assistantsPage.modals.createWithTemplate')}
                </Button>
                <Button
                  onClick={() => handleResetAndSetStep(AddAPTab.AP_FORM)}
                  variant="contained"
                  disabled={isSubmitting}>
                  {t('assistantsPage.modals.create')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleResetAndSetStep(AddAPTab.SELECT_AP)}
                  disabled={isSubmitting}>
                  {t('assistantsPage.modals.back')}
                </Button>
                <Button onClick={handleClose} disabled={isSubmitting}>
                  {t('assistantsPage.modals.cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting
                    ? t('assistantsPage.modals.creating')
                    : t('assistantsPage.modals.create')}
                </Button>
              </>
            )}
          </DialogActions>
        </FormProvider>
      </Box>
    </Dialog>
  );
};
